"use client";

/**
 * Formulaire « Demander un rendez-vous » des fiches clientes injoignables
 * (aucun téléphone, WhatsApp ni RDV en ligne) — cf. lib/booking/appointment-request.ts.
 * Envoie un lead APPOINTMENT_REQUEST à POST /api/leads (mécanisme existant :
 * espace Pro du médecin, alerte e-mail de l'équipe, e-mail au cabinet si connu).
 *
 * Libellés reçus en props depuis le serveur (getTranslations « booking.request ») :
 * rien à ajouter au NextIntlClientProvider (même choix que HomeSamplingForm).
 * Validation par champ (aria-invalid + aria-describedby, focus sur le premier
 * champ en erreur) ; en cas de succès, le message reçoit le focus.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/i18n/navigation";
import { interpretLeadResponse } from "@/components/mre/lead-request";
import { track } from "@/lib/observability/analytics";
import {
  APPOINTMENT_REQUEST_FIELD_ORDER,
  buildAppointmentRequestLead,
  validateAppointmentRequest,
  type AppointmentRequestError,
  type AppointmentRequestField,
} from "@/lib/booking/appointment-request";

export type AppointmentRequestLabels = {
  firstName: string;
  lastName: string;
  optional: string;
  phone: string;
  phoneHint: string;
  email: string;
  emailHint: string;
  message: string;
  messagePlaceholder: string;
  consent: string;
  consentStorage: string;
  privacyLink: string;
  urgent: string;
  submit: string;
  sending: string;
  success: string;
  successFollowUp: string;
  required: string;
  phoneInvalid: string;
  emailInvalid: string;
  consentRequired: string;
  fixErrors: string;
  tooMany: string;
  error: string;
};

type Props = {
  physicianSlug: string;
  locale: string;
  /** Surface d'origine, pour Lead.source (« rdv-request:{surface} »). */
  surface: string;
  labels: AppointmentRequestLabels;
  tier?: string;
  city?: string;
};

const ID: Record<AppointmentRequestField, string> = {
  firstName: "ar-first-name",
  lastName: "ar-last-name",
  phone: "ar-phone",
  email: "ar-email",
  message: "ar-message",
  consent: "ar-consent",
};

const field =
  "w-full rounded-[12px] border border-[var(--sahha-border-control)] bg-[var(--sahha-surface-0)] px-3.5 py-2.5 text-[15px] text-[var(--sahha-text-900)] placeholder:text-[var(--sahha-text-400)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-700)] aria-[invalid=true]:border-[var(--sahha-danger-text)]";
const labelCls = "mb-1.5 block text-[13.5px] font-semibold text-[var(--sahha-text-800)]";
const errCls = "mt-1 text-[12.5px] font-semibold text-[var(--sahha-danger-text)]";
const hintCls = "mt-1 text-[12.5px] text-[var(--sahha-text-500)]";

export function AppointmentRequestForm({ physicianSlug, locale, surface, labels: t, tier, city }: Props) {
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [errors, setErrors] = useState<Partial<Record<AppointmentRequestField, AppointmentRequestError>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (state === "sent") successRef.current?.focus();
  }, [state]);

  const message = (code: AppointmentRequestError): string =>
    code === "phoneInvalid" ? t.phoneInvalid
      : code === "emailInvalid" ? t.emailInvalid
        : code === "consentRequired" ? t.consentRequired
          : t.required;

  const focusFirst = (errs: Partial<Record<AppointmentRequestField, AppointmentRequestError>>) => {
    const first = APPOINTMENT_REQUEST_FIELD_ORDER.find((f) => errs[f]);
    if (first) (formRef.current?.querySelector(`#${ID[first]}`) as HTMLElement | null)?.focus();
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current) return;
    const f = new FormData(e.currentTarget);
    const fields = {
      firstName: String(f.get("firstName") ?? ""),
      lastName: String(f.get("lastName") ?? ""),
      phone: String(f.get("phone") ?? ""),
      email: String(f.get("email") ?? ""),
      message: String(f.get("message") ?? ""),
      consent: f.get("consent") !== null,
    };
    const errs = validateAppointmentRequest(fields);
    setErrors(errs);
    setFormError(null);
    if (Object.keys(errs).length) {
      focusFirst(errs);
      return;
    }
    inFlight.current = true;
    setState("sending");
    const body = buildAppointmentRequestLead(fields, { physicianSlug, locale, surface });
    let outcome: ReturnType<typeof interpretLeadResponse> | { kind: "network" };
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(body),
      });
      outcome = interpretLeadResponse(res.status, await res.json().catch(() => null));
    } catch {
      outcome = { kind: "network" };
    } finally {
      inFlight.current = false;
    }
    if (outcome.kind === "success") {
      setState("sent");
      track("lead:submitted", { type: "APPOINTMENT_REQUEST", source: body.source, physicianSlug, tier, city });
      return;
    }
    setState("idle");
    if (outcome.kind === "rateLimited") {
      setFormError(t.tooMany);
      return;
    }
    if (outcome.kind === "invalid") {
      const known = outcome.fields.filter((k): k is AppointmentRequestField => k in ID);
      if (known.length) {
        const errs2: Partial<Record<AppointmentRequestField, AppointmentRequestError>> = {};
        for (const k of known) errs2[k] = k === "phone" ? "phoneInvalid" : k === "email" ? "emailInvalid" : "required";
        setErrors(errs2);
        focusFirst(errs2);
        return;
      }
    }
    setFormError(t.error);
  }

  if (state === "sent") {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        role="status"
        className="rounded-[18px] bg-[var(--sahha-success-bg)] p-5 text-[var(--sahha-text-900)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sahha-primary-700)]"
      >
        <p className="text-[15px] font-semibold">{t.success}</p>
        <p className="mt-2 text-[13.5px] text-[var(--sahha-text-700)]">{t.successFollowUp}</p>
      </div>
    );
  }

  const describedBy = (k: AppointmentRequestField, hint?: string) =>
    [errors[k] ? `${ID[k]}-err` : null, hint ?? null].filter(Boolean).join(" ") || undefined;
  const errorOf = (k: AppointmentRequestField) =>
    errors[k] ? <p id={`${ID[k]}-err`} className={errCls}>{message(errors[k]!)}</p> : null;
  const hasErrors = Object.keys(errors).length > 0;
  // Un champ corrigé perd son message d'erreur.
  const onChange = (e: React.FormEvent<HTMLFormElement>) => {
    const k = (e.target as HTMLInputElement).name as AppointmentRequestField;
    if (errors[k]) setErrors((prev) => { const next = { ...prev }; delete next[k]; return next; });
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} onChange={onChange} noValidate className="grid gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor={ID.firstName} className={labelCls}>{t.firstName}</label>
        <input id={ID.firstName} name="firstName" required maxLength={100} autoComplete="given-name" className={field}
          aria-invalid={errors.firstName ? true : undefined} aria-describedby={describedBy("firstName")} />
        {errorOf("firstName")}
      </div>
      <div>
        <label htmlFor={ID.lastName} className={labelCls}>
          {t.lastName} <span className="font-normal text-[var(--sahha-text-500)]">({t.optional})</span>
        </label>
        <input id={ID.lastName} name="lastName" maxLength={100} autoComplete="family-name" className={field} />
      </div>
      <div>
        <label htmlFor={ID.phone} className={labelCls}>{t.phone}</label>
        <input id={ID.phone} name="phone" type="tel" required inputMode="tel" autoComplete="tel" dir="ltr" placeholder="06 12 34 56 78"
          className={`${field} text-start rtl:text-end`}
          aria-invalid={errors.phone ? true : undefined} aria-describedby={describedBy("phone", `${ID.phone}-hint`)} />
        {errorOf("phone")}
        <p id={`${ID.phone}-hint`} className={hintCls}>{t.phoneHint}</p>
      </div>
      <div>
        <label htmlFor={ID.email} className={labelCls}>
          {t.email} <span className="font-normal text-[var(--sahha-text-500)]">({t.optional})</span>
        </label>
        <input id={ID.email} name="email" type="email" inputMode="email" autoComplete="email" dir="ltr" maxLength={254}
          className={`${field} text-start rtl:text-end`}
          aria-invalid={errors.email ? true : undefined} aria-describedby={describedBy("email", `${ID.email}-hint`)} />
        {errorOf("email")}
        <p id={`${ID.email}-hint`} className={hintCls}>{t.emailHint}</p>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={ID.message} className={labelCls}>
          {t.message} <span className="font-normal text-[var(--sahha-text-500)]">({t.optional})</span>
        </label>
        <textarea id={ID.message} name="message" rows={3} maxLength={1500} placeholder={t.messagePlaceholder} className={field} />
      </div>
      <div className="sm:col-span-2">
        <div className="flex items-start gap-2.5">
          <input id={ID.consent} type="checkbox" name="consent" required className="mt-1 h-4 w-4 shrink-0 accent-[var(--sahha-primary-700)]"
            aria-invalid={errors.consent ? true : undefined} aria-describedby={describedBy("consent", `${ID.consent}-info`)} />
          <label htmlFor={ID.consent} className="text-[13.5px] leading-relaxed text-[var(--sahha-text-700)]">{t.consent}</label>
        </div>
        <p id={`${ID.consent}-info`} className="mt-1.5 ps-[26px] text-[12.5px] leading-relaxed text-[var(--sahha-text-600)]">
          {t.consentStorage}{" "}
          <Link href="/politique-confidentialite" className="font-semibold text-[var(--sahha-primary-800)] underline underline-offset-2">{t.privacyLink}</Link>
        </p>
        {errors.consent && <p id={`${ID.consent}-err`} className={`${errCls} ps-[26px]`}>{message(errors.consent)}</p>}
      </div>
      {(formError || hasErrors) && (
        <p role="alert" className="text-[13.5px] font-semibold text-[var(--sahha-danger-text)] sm:col-span-2">{formError ?? t.fixErrors}</p>
      )}
      <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          aria-disabled={state === "sending"}
          className="w-full rounded-full bg-[var(--sahha-primary-700)] px-6 py-3 text-[15px] font-bold text-white transition hover:bg-[var(--sahha-primary-800)] active:scale-[0.99] aria-disabled:opacity-70 sm:w-auto"
        >
          {state === "sending" ? t.sending : t.submit}
        </button>
        <p className="text-[12.5px] text-[var(--sahha-text-600)]">{t.urgent}</p>
      </div>
    </form>
  );
}
